"use client";

import { c as _c } from "react/compiler-runtime";
import type { Appearance } from "@stripe/stripe-js";
import { useMemo } from "react";
import { getCssVar } from "@/components/rainforest/use-rainforest-theme";
import { ResolvedTheme, useTheme } from "@/contexts/theme-provider";

/**
 * The app's theme, expressed as Stripe's Appearance API.
 *
 * Elements render in a cross-origin iframe, so no stylesheet of ours reaches
 * them and `theme: "stripe"` - the default, and what both surfaces hardcoded -
 * is a white card form. On a dark console that is a lit rectangle in the middle
 * of the page, and under a tenant's custom theme it is the wrong brand
 * entirely. The Appearance API is the only channel into that iframe.
 *
 * This is the Stripe half of what `rainforest/use-rainforest-theme.ts` already
 * does for Rainforest's web component: same CSS variables, same reader, so both
 * processors' card forms answer to one set of tokens rather than each carrying
 * its own palette.
 *
 * Read from computed styles rather than a static light/dark map because a
 * tenant's custom theme sets these variables as inline properties on <html>.
 * A table keyed on the mode would ignore that and quietly serve the default
 * palette to every clinic that picked its own.
 */
function buildAppearance(resolvedTheme: ResolvedTheme): Appearance {
  // Stripe's own dark base, so the parts not mapped below - hover states,
  // shadows, the payment-method dropdown's chrome - land dark too, rather than
  // being a light form with dark text poured over it.
  const theme = resolvedTheme === "dark" ? "night" : "stripe";

  // Server render: there is no <html> to read variables off. The base theme
  // alone is still the right answer for the mode, and the memo below re-runs on
  // the client where the variables exist.
  if (typeof document === "undefined") return {
    theme
  };
  const border = getCssVar("--border");
  const ring = getCssVar("--ring");
  return {
    theme,
    // No `fontFamily`: the console's Switzer is a local font, and an iframe can
    // only use a font Stripe can fetch through the `fonts` option. Naming it
    // here would select a fallback and claim otherwise.
    variables: {
      colorPrimary: getCssVar("--primary"),
      // Our own `Input` is `bg-transparent` and shows whatever is behind it;
      // an Element cannot be. The page background is the closest single answer
      // and the one the Rainforest hook already gives the other processor.
      colorBackground: getCssVar("--background"),
      colorText: getCssVar("--foreground"),
      colorTextSecondary: getCssVar("--foreground-secondary"),
      colorTextPlaceholder: getCssVar("--foreground-muted"),
      colorDanger: getCssVar("--destructive"),
      colorSuccess: getCssVar("--status-success-text"),
      iconColor: getCssVar("--foreground-muted"),
      accessibleColorOnColorPrimary: getCssVar("--primary-foreground"),
      // `rounded-xl`, which is what `components/ui/input.tsx` uses.
      borderRadius: "12px"
    },
    rules: {
      ".Input": {
        borderColor: border
      },
      ".Input:focus": {
        borderColor: ring,
        boxShadow: `0 0 0 1px ${ring}`
      },
      ".Label": {
        color: getCssVar("--foreground-secondary")
      },
      ".Tab": {
        borderColor: border
      },
      ".Tab--selected": {
        borderColor: ring
      }
    }
  };
}

/** Appearance options for `<Elements>`, tracking the active theme. */
export function useStripeAppearance() {
  const $ = _c(2);
  const {
    resolvedTheme,
    customTheme
  } = useTheme();
  let t0;
  if ($[0] !== resolvedTheme) {
    t0 = buildAppearance(resolvedTheme);
    $[0] = resolvedTheme;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return t0;
}